import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import DataTable from './common/DataTable';
import DataTableWithFilters from './common/DataTableWithFilters';
import ConversationModal from './common/ConversationModal';
import Alert from './common/Alert';
import LoadingSpinner from './common/LoadingSpinner';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import AsistenteService from '../services/AsistenteService';
import AsistentesClasicosService from '../services/AsistentesClasicosService';
import ThreadsService from '../services/ThreadsService';
import RegionalService from '../services/RegionalService';
import { filterAdminMenuItems } from '../utils/adminMenu';
import ProcesosMenu from './common/ProcesosMenu';
import './Reports.css';

const isReportsUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return (
    roleName === 'profesor' ||
    roleName.includes('profesor') ||
    roleName.includes('profe') ||
    roleName === 'admin' ||
    roleName.includes('adminarea') ||
    roleName.includes('admin area') ||
    roleName.includes('admin')
  );
};

const isAdminUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'admin' || roleName.includes('adminarea') || roleName.includes('admin area') || roleName.includes('admin');
};

const adminMenuItems = [
  { id: 'resumen', label: 'Dashboard', icon: 'fas fa-chart-line' },
  { id: 'profesores', label: 'Profesores', icon: 'fas fa-chalkboard-teacher' },
  // { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate' },
  { id: 'areas', label: 'Areas', icon: 'fas fa-sitemap' },
  { id: 'materias', label: 'Materias', icon: 'fas fa-book' },
  { id: 'asistentes', label: 'Asistentes OpenAI', icon: 'fas fa-robot' }
];

const Reports = () => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canAccessReports = isReportsUser(user);
  const isAdmin = isAdminUser(user);
  const filteredAdminMenuItems = filterAdminMenuItems(adminMenuItems, user);
  const roleLabel = isAdmin ? (String(user?.roleName || user?.nombreRol || user?.role || 'Admin')) : 'Profesor';
  
  // Estados
  const [asistentes, setAsistentes] = useState([]);
  const [asistentesClasicos, setAsistentesClasicos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [showThreadsView, setShowThreadsView] = useState(false);
  
  const [isGptClasico, setIsGptClasico] = useState(false);
  const [gptClasicoBusqueda, setGptClasicoBusqueda] = useState('');
  const gptClasicoBusquedaRef = useRef('');
  const debounceTimerRef = useRef(null);

  // Estados para mensajes
  const [mensajes, setMensajes] = useState([]);
  const [isLoadingMensajes, setIsLoadingMensajes] = useState(false);
  const [paginacionMensajes, setPaginacionMensajes] = useState({
    paginaActual: 1,
    tamañoPagina: 50,
    total: 0,
    totalPaginas: 0
  });
  const [mensajesStats, setMensajesStats] = useState({
    total: 0,
    user: 0,
    assistant: 0,
    threads: 0
  });
  const isLoadingRef = useRef(false);
  
  // Estados paral modal de conversación
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const normalizeAsistente = (item) => {
    if (!item) return item;
    const fechaRaw = item.fechaAlta || item.FechaAlta || item.fechaCreacion || item.FechaCreacion;
    const fechaAlta = fechaRaw ? new Date(fechaRaw) : null;
    return {
      ...item,
      idOpenAIAssistant: item.idOpenAIAssistant || item.IdOpenAIAssistant,
      nombreAsistente:
        item.nombreAsistente ||
        item.NombreAsistente ||
        item.gptClasicoNombre ||
        item.GPTClasicoNombre ||
        item.nombre ||
        item.Nombre ||
        '—',
      nombreArea: item.nombreArea || item.NombreArea || item.areaNombre || item.AreaNombre || item.area || item.Area,
      nombreMateria: item.nombreMateria || item.NombreMateria || item.materiaNombre || item.MateriaNombre || item.materia || item.Materia,
      fechaAlta
    };
  };

  const loadAsistentes = async () => {
    if (authLoading) return;
    if (!canAccessReports) {
      setIsLoading(false);
      return;
    }
    // Evitar llamadas duplicadas
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true; // Mover aquí para evitar race conditions
    
    try {
      setIsLoading(true);
      setAlert({ show: false, message: '', type: 'error' });

      const [openAiResp, clasicosResp] = await Promise.all([
        AsistenteService.getMisAsistentes().catch((error) => ({ ok: false, error: error.message || String(error) })),
        isGptClassicEnabled
          ? AsistentesClasicosService.getMisAsistentes().catch((error) => ({ ok: false, error: error.message || String(error) }))
          : Promise.resolve({ ok: true, asistentes: [] })
      ]);

      if (openAiResp.ok && openAiResp.asistentes) {
        setAsistentes(openAiResp.asistentes.map(normalizeAsistente));
      } else {
        setAsistentes([]);
        if (openAiResp.error) {
          throw new Error(openAiResp.error || 'Error al cargar los asistentes OpenAI');
        }
      }

      if (clasicosResp.ok && clasicosResp.asistentes) {
        setAsistentesClasicos(clasicosResp.asistentes.map(normalizeAsistente));
      } else {
        setAsistentesClasicos([]);
        if (clasicosResp.error) {
          setAlert({
            show: true,
            message: clasicosResp.error || 'Error al cargar los asistentes GPT',
            type: 'error'
          });
        }
      }
      
    } catch (error) {
      console.error('❌ Reports - Error al cargar asistentes:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los asistentes. Inténtalo nuevamente.',
        type: 'error'
      });
      setAsistentes([]);
      setAsistentesClasicos([]);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Cargar asistentes al montar el componente
  useEffect(() => {
    if (authLoading) return;
    if (!canAccessReports) {
      setIsLoading(false);
      return;
    }
    loadAsistentes();
  }, [canAccessReports, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps



  const handleRefresh = () => {
    if (!canAccessReports || authLoading) return;
    isLoadingRef.current = false; // Resetear el flag de carga
    loadAsistentes();
  };

  const handleLogout = () => {
    logout();
  };

  const handleNavigate = (path, options = {}) => {
    setIsSidebarOpen(false);
    navigate(path, options);
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  // Función para manejar la acción de ver mensajes
  const handleViewMessages = async (asistente) => {
    if (!canAccessReports) return;
    setSelectedAssistant(asistente);
    setShowThreadsView(true);

    const openAIId = asistente.idOpenAIAssistant;

    if (!openAIId) {
      setAlert({
        show: true,
        message: 'Error: No se encontró el ID del asistente',
        type: 'error'
      });
      return;
    }

    const esGptClasico = !!(asistente.gptClasicoCreacionFinalizada ?? asistente.GPTClasicoCreacionFinalizada);
    setIsGptClasico(esGptClasico);
    setGptClasicoBusqueda('');
    gptClasicoBusquedaRef.current = '';

    if (esGptClasico) {
      await loadMensajesGptClasico(openAIId);
    } else {
      await loadMensajesAsistente(openAIId);
    }
  };

  const buildRowsFromThreads = (threads) => {
    if (!Array.isArray(threads)) return [];

    const rows = [];
    threads.forEach(thread => {
      const mensajesThread = Array.isArray(thread.mensajes) ? thread.mensajes : [];
      for (let i = 0; i < mensajesThread.length; i++) {
        const msg = mensajesThread[i];
        if (msg.rol !== 'user') continue;

        let respuesta = null;
        for (let j = i + 1; j < mensajesThread.length; j++) {
          if (mensajesThread[j].rol === 'assistant') {
            respuesta = mensajesThread[j];
            break;
          }
        }

        rows.push({
          ...msg,
          threadId: thread.idThread,
          fechaCreacionThread: thread.fechaCreacion ? new Date(thread.fechaCreacion) : null,
          fechaEnviado: msg.fechaEnvio ? new Date(msg.fechaEnvio) : null,
          fecha: msg.fechaEnvio ? new Date(msg.fechaEnvio) : null,
          mensaje: msg.contenido || '',
          respuesta: respuesta?.contenido || '',
          usuario: 'Estudiante',
          rol: msg.rol
        });
      }
    });

    return rows;
  };

  // Función para cargar los mensajes del asistente
  const loadMensajesAsistente = async (asistenteId, pagina = 1) => {
    if (!canAccessReports) {
      setIsLoadingMensajes(false);
      return;
    }
    setIsLoadingMensajes(true);
    try {
      const response = await ThreadsService.getThreadsMessagesFromDb(
        asistenteId, 
        '', 
        pagina, 
        paginacionMensajes.tamañoPagina, 
        100
      );
      
      const rows = buildRowsFromThreads(response.threads || []);
      setMensajes(rows);

      const totalMessages = response.totalMessages || response.total || 0;
      const threadsCount = response.totalThreads || 0;
      const userCount = rows.length;
      const assistantCount = Math.max(totalMessages - userCount, 0);
      setMensajesStats({
        total: totalMessages,
        user: userCount,
        assistant: assistantCount,
        threads: threadsCount
      });
      
      // Actualizar información de paginación con la nueva estructura
      setPaginacionMensajes({
        paginaActual: response.paginaActual || pagina,
        tamañoPagina: response.tamañoPagina || paginacionMensajes.tamañoPagina,
        total: totalMessages,
        totalPaginas: response.totalPaginas || Math.ceil(totalMessages / (response.tamañoPagina || paginacionMensajes.tamañoPagina)),
        totalThreads: response.totalThreads || 0,
        totalThreadsEnBD: response.totalThreadsEnBD || 0
      });
      
    } catch (error) {
      console.error('Error cargando mensajes:', error);
      setAlert({
        show: true,
        message: 'Error al cargar los mensajes del asistente',
        type: 'error'
      });
      setMensajes([]);
      setPaginacionMensajes(prev => ({
        ...prev,
        total: 0,
        totalPaginas: 0
      }));
      setMensajesStats({ total: 0, user: 0, assistant: 0, threads: 0 });
    } finally {
      setIsLoadingMensajes(false);
    }
  };

  // Función para cargar mensajes GPT Clásico
  const loadMensajesGptClasico = async (asistenteId, pagina = 1, busqueda = '') => {
    if (!canAccessReports) {
      setIsLoadingMensajes(false);
      return;
    }
    setIsLoadingMensajes(true);
    try {
      const response = await ThreadsService.getGptClasicoMensajes(
        asistenteId,
        pagina,
        paginacionMensajes.tamañoPagina,
        busqueda
      );

      setMensajes(response.mensajes || []);
      setMensajesStats({
        total: response.totalMensajes || 0,
        user: 0,
        assistant: 0,
        threads: 0
      });
      setPaginacionMensajes({
        paginaActual: response.paginaActual || pagina,
        tamañoPagina: response.tamañoPagina || paginacionMensajes.tamañoPagina,
        total: response.totalMensajes || 0,
        totalPaginas: response.totalPaginas || 0
      });
    } catch (error) {
      console.error('Error cargando mensajes GPT clásico:', error);
      setAlert({
        show: true,
        message: 'Error al cargar los mensajes del GPT Clásico',
        type: 'error'
      });
      setMensajes([]);
      setPaginacionMensajes(prev => ({ ...prev, total: 0, totalPaginas: 0 }));
      setMensajesStats({ total: 0, user: 0, assistant: 0, threads: 0 });
    } finally {
      setIsLoadingMensajes(false);
    }
  };

  // Función para volver a la vista de asistentes
  const handleBackToAssistants = () => {
    setShowThreadsView(false);
    setSelectedAssistant(null);
    setIsGptClasico(false);
    setGptClasicoBusqueda('');
    gptClasicoBusquedaRef.current = '';
    setMensajes([]);
    setPaginacionMensajes({
      paginaActual: 1,
      tamañoPagina: 50,
      total: 0,
      totalPaginas: 0
    });
  };

  useEffect(() => {
    if (!canAccessReports || !location?.state) return;
    const assistantFromState = location.state.assistant || null;
    const assistantId = assistantFromState?.idOpenAIAssistant
      || assistantFromState?.IdOpenAIAssistant
      || location.state.assistantId;
    if (!assistantId) return;
    const assistantName = assistantFromState?.nombreAsistente
      || assistantFromState?.nombre
      || assistantFromState?.Nombre
      || location.state.assistantName
      || 'Asistente';
    const assistantPayload = assistantFromState || { idOpenAIAssistant: assistantId, nombreAsistente: assistantName };
    const esGpt = !!(assistantPayload.gptClasicoCreacionFinalizada ?? assistantPayload.GPTClasicoCreacionFinalizada);
    setSelectedAssistant(assistantPayload);
    setShowThreadsView(true);
    setIsGptClasico(esGpt);
    if (esGpt) {
      loadMensajesGptClasico(assistantId);
    } else {
      loadMensajesAsistente(assistantId);
    }
  }, [canAccessReports, location]);

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!canAccessReports) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Los reportes están disponibles únicamente para usuarios con rol Profesor o Admin.</p>
        </div>
      </div>
    );
  }

  // Función para manejar cambio de página en mensajes
  const handlePageChange = (nuevaPagina) => {
    if (selectedAssistant && nuevaPagina !== paginacionMensajes.paginaActual) {
      const openAIId = selectedAssistant.idOpenAIAssistant;
      if (openAIId) {
        if (isGptClasico) {
          loadMensajesGptClasico(openAIId, nuevaPagina, gptClasicoBusquedaRef.current);
        } else {
          loadMensajesAsistente(openAIId, nuevaPagina);
        }
      }
    }
  };

  // Función para manejar búsqueda en GPT Clásico con debounce
  const handleGptClasicoBusqueda = (e) => {
    const valor = e.target.value;
    setGptClasicoBusqueda(valor);
    gptClasicoBusquedaRef.current = valor;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (selectedAssistant?.idOpenAIAssistant) {
        loadMensajesGptClasico(selectedAssistant.idOpenAIAssistant, 1, valor);
      }
    }, 400);
  };

  // Función para manejar la visualización de conversación completa
  const handleViewConversation = (message, source) => {
    setSelectedMessage({ ...message, conversationSource: source });
    setShowConversationModal(true);
  };  // Función para cerrar el modal de conversación
  const handleCloseConversationModal = () => {
    setShowConversationModal(false);
    setSelectedMessage(null);
  };

  // Función para remover etiquetas HTML y mostrar solo texto plano
  const stripHtmlTags = (html) => {
    if (!html) return '';
    
    // Crear un elemento temporal para usar la API del navegador
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Obtener solo el texto sin etiquetas
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Limpiar espacios excesivos y saltos de línea
    return textContent.replace(/\s+/g, ' ').trim();
  };

  const handleBackToList = () => {
    setShowThreadsView(false);
    setSelectedAssistant(null);
    setIsGptClasico(false);
    setGptClasicoBusqueda('');
    gptClasicoBusquedaRef.current = '';
    setMensajes([]);
    setPaginacionMensajes({
      paginaActual: 1,
      tamañoPagina: 50,
      total: 0,
      totalPaginas: 0
    });
    setMensajesStats({ total: 0, user: 0, assistant: 0, threads: 0 });
  };

  // Formatear fecha para mostrar
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    
    return fecha.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const featureFlags = RegionalService.getFeatureFlags();
  const canOpenAIHistory = featureFlags.some(flag => {
    const nombre = flag?.nombre || flag?.Nombre;
    const activo = flag?.activo ?? flag?.Activo;
    return nombre === 'PermiteObtenerHistorialDesdeOpenAI' && Boolean(activo);
  });
  const isGptClassicEnabled = featureFlags.some((flag) => {
    const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
    return nombre === 'asistentesclasicos gpt' && Boolean(flag?.activo ?? flag?.Activo);
  });

  const activeRegionalId = RegionalService.getActiveRegionalId();

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del Asistente',
      sortable: true,
      className: 'font-weight-bold'
    },
    {
      key: 'tipo',
      title: 'Tipo',
      sortable: true,
      align: 'center',
      width: 'sm',
      render: (value, item) => {
        const esGpt = item.gptClasicoCreacionFinalizada ?? item.GPTClasicoCreacionFinalizada;
        return esGpt
          ? <span className="user-badge assistant" style={{ fontSize: '0.75rem' }}>GPT Clásico</span>
          : <span className="user-badge student" style={{ fontSize: '0.75rem' }}>OpenAI</span>;
      }
    },
    {
      key: 'nombreArea',
      title: 'Área',
      sortable: true
    },
    {
      key: 'nombreMateria',
      title: 'Materia',
      sortable: true
    },
    {
      key: 'fechaAlta',
      title: 'Fecha de Creación',
      sortable: true,
      align: 'center',
      width: 'lg',
      className: 'date-cell',
      formatter: formatFecha
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-messages"
            title="Ver mensajes del asistente"
            onClick={() => handleViewMessages(item)}
          >
            <i className="fas fa-comments"></i>
          </button>
        </div>
      )
    }
  ];

  // Definir las columnas para la tabla de mensajes
  const columnasMensajes = [
    {
      key: 'fechaEnviado',
      title: 'Fecha de Envío',
      sortable: true,
      align: 'center',
      width: 'md',
      className: 'date-cell',
      formatter: (fecha) => {
        if (!fecha) return 'N/A';
        const fechaObj = new Date(fecha);
        return fechaObj.toLocaleDateString('es-AR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      key: 'usuario',
      title: 'Tipo de Usuario',
      sortable: true,
      className: 'font-weight-bold user-type-cell',
      formatter: (usuario, item) => {
        const rol = item.rol || 'unknown';
        if (rol === 'user') {
          return (
            <span className="user-badge student">
              <i className="fas fa-user-graduate"></i>
              Estudiante
            </span>
          );
        } else if (rol === 'assistant') {
          return (
            <span className="user-badge assistant">
              <i className="fas fa-robot"></i>
              Asistente
            </span>
          );
        }
        return usuario || 'Usuario';
      }
    },
    {
      key: 'mensaje',
      title: 'Mensaje Enviado',
      sortable: false,
      className: 'message-cell',
      formatter: (mensaje) => {
        // Limpiar HTML del mensaje y truncar si es necesario
        const mensajeTexto = stripHtmlTags(mensaje || '');
        return mensajeTexto.length > 200 
          ? `${mensajeTexto.substring(0, 200)}...` 
          : mensajeTexto;
      }
    },
    {
      key: 'respuesta',
      title: 'Respuesta del Asistente',
      sortable: false,
      className: 'message-cell',
      formatter: (mensaje) => {
        const mensajeTexto = stripHtmlTags(mensaje || '');
        return mensajeTexto.length > 200
          ? `${mensajeTexto.substring(0, 200)}...`
          : mensajeTexto || '-';
      }
    },
    {
      key: 'threadId',
      title: 'Thread ID',
      sortable: true,
      align: 'center',
      width: 'sm',
      className: 'thread-id-cell',
      formatter: (threadId) => {
        if (!threadId) return 'N/A';
        // Mostrar solo los últimos 8 caracteres para que sea más legible
        return threadId.length > 8 ? `...${threadId.slice(-8)}` : threadId;
      }
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'sm',
      className: 'actions-cell',
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-view"
            title="Ver conversación completa (BD)"
            onClick={() => handleViewConversation(item, 'db')}
          >
            <i className="fas fa-database"></i>
          </button>
          {canOpenAIHistory && (
            <button
              className="table-btn table-btn-view"
              title="Ver conversación completa (OpenAI)"
              onClick={() => handleViewConversation(item, 'openai')}
            >
              <i className="fas fa-cloud"></i>
            </button>
          )}
        </div>
      )
    }
  ];

  // Columnas para mensajes GPT Clásico (sin acciones)
  const columnasGptClasico = [
    {
      key: 'createdAt',
      title: 'Fecha',
      sortable: true,
      align: 'center',
      width: 'md',
      className: 'date-cell',
      formatter: (fecha) => {
        if (!fecha) return 'N/A';
        const fechaObj = new Date(fecha);
        return fechaObj.toLocaleDateString('es-AR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      key: 'remitente',
      title: 'Remitente',
      sortable: true,
      className: 'font-weight-bold user-type-cell',
      formatter: (remitente) => {
        const r = (remitente || '').toLowerCase();
        if (r === 'user' || r === 'usuario' || r === 'estudiante') {
          return (
            <span className="user-badge student">
              <i className="fas fa-user-graduate"></i>
              Estudiante
            </span>
          );
        } else if (r === 'assistant' || r === 'asistente') {
          return (
            <span className="user-badge assistant">
              <i className="fas fa-robot"></i>
              Asistente
            </span>
          );
        }
        return remitente || '—';
      }
    },
    {
      key: 'mensaje',
      title: 'Mensaje',
      sortable: false,
      className: 'message-cell',
      formatter: (mensaje) => {
        const texto = stripHtmlTags(mensaje || '');
        return texto.length > 200 ? `${texto.substring(0, 200)}...` : texto || '—';
      }
    },
    {
      key: 'conversationId',
      title: 'Conversación',
      sortable: true,
      align: 'center',
      width: 'sm',
      className: 'thread-id-cell',
      formatter: (id) => {
        if (!id) return 'N/A';
        return id.length > 8 ? `...${id.slice(-8)}` : id;
      }
    }
  ];

  if (showThreadsView && selectedAssistant) {
    return (
      <div className={`new-dashboard reports ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <i className="fas fa-graduation-cap"></i>
              <span>UTN FRC</span>
            </div>
          </div>
          
          <nav className="sidebar-nav">
            {isAdmin ? (
              filteredAdminMenuItems.map((item) => (
                <button
                  key={item.id}
                  className={`nav-item ${item.id === 'reportes' ? 'active' : ''}`}
                  onClick={() => handleNavigate('/admin-dashboard', { state: { tab: item.id } })}
                >
                  <i className={item.icon}></i>
                  <span>{item.label}</span>
                </button>
              ))
            ) : (
              <>
                <button
                  className="nav-item"
                  onClick={() => handleNavigate('/dashboard')}
                >
                  <i className="fas fa-tachometer-alt"></i>
                  <span>Dashboard</span>
                </button>
                <button
                  className="nav-item"
                  onClick={() => handleNavigate('/my-assistants')}
                >
                  <i className="fas fa-list-ul"></i>
                  <span>Asistentes OpenAI</span>
                </button>
                {isGptClassicEnabled && (
                  <button
                    className="nav-item"
                    onClick={() => handleNavigate('/gpt-assistants')}
                  >
                    <i className="fas fa-robot"></i>
                    <span>Asistentes GPT</span>
                  </button>
                )}
                <button className="nav-item active">
                  <i className="fas fa-chart-bar"></i>
                  <span>Reportes</span>
                </button>
              </>
            )}
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-user">
              <div className="user-avatar">
                <i className="fas fa-user"></i>
              </div>
              <div className="user-info">
                <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                <span className="user-role">{roleLabel}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content - Threads View */}
        <main className="dashboard-main">
          <header className="dashboard-header">
            <div className="header-left">
              <button
                type="button"
                className="sidebar-toggle"
                onClick={() => setIsSidebarOpen((prev) => !prev)}
                title="Mostrar menú"
              >
                <i className="fas fa-bars"></i>
              </button>
              <button 
                className="btn btn-back"
                onClick={handleBackToList}
                title="Volver a la lista"
              >
                <i className="fas fa-arrow-left"></i>
                Volver
              </button>
              <div className="header-info">
                <h1 className="page-title">
                  <i className="fas fa-comments"></i>
                  Mensajes: {selectedAssistant.nombreAsistente}
                </h1>
                <p className="page-subtitle">Historial completo de conversaciones con estudiantes</p>
              </div>
            </div>
            
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar datos"
              >
                <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
                Actualizar
              </button>
              
              <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
                <i className="fas fa-sign-out-alt"></i>
                <span className="logout-text">Salir</span>
              </button>
            </div>
          </header>

          <div className="dashboard-content">
            {/* Alert */}
            {alert.show && (
              <Alert
                title={alert.title}
                message={alert.message}
                type={alert.type}
                onClose={closeAlert}
              />
            )}

            {/* Mensajes Content */}
            <div className="content-card">
              <div className="card-header">
                <div className="card-header-left">
                  <h2 className="card-title">
                    <i className="fas fa-comments"></i>
                    {isGptClasico ? 'Mensajes GPT Clásico' : 'Mensajes de Estudiantes'}
                  </h2>
                  <p className="card-subtitle">
                    {isGptClasico
                      ? <>Mensajes del GPT Clásico <strong>{selectedAssistant.nombreAsistente}</strong></>
                      : <>Mensajes enviados por estudiantes al asistente <strong>{selectedAssistant.nombreAsistente}</strong></>
                    }
                  </p>
                </div>
                <div className="card-header-right">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleBackToAssistants}
                    title="Volver a la lista de asistentes"
                  >
                    <i className="fas fa-arrow-left"></i>
                    Volver
                  </button>
                </div>
              </div>

              {/* Estadísticas de mensajes */}
              {!isLoadingMensajes && (paginacionMensajes.total > 0 || mensajes.length > 0) && (
                <div className="messages-stats">
                  <div className="stat-item">
                    <span className="stat-number">{mensajesStats.total || 0}</span>
                    <span className="stat-label">Total Mensajes</span>
                  </div>
                  {!isGptClasico && (
                    <>
                      <div className="stat-item">
                        <span className="stat-number">{mensajesStats.user || 0}</span>
                        <span className="stat-label">De Estudiantes</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{mensajesStats.assistant || 0}</span>
                        <span className="stat-label">Del Asistente</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">
                          {mensajesStats.threads || new Set(mensajes.map(m => m.threadId)).size}
                        </span>
                        <span className="stat-label">Threads Activos</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="card-content">
                {isGptClasico && (
                  <div style={{ marginBottom: '1rem' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar en mensajes..."
                      value={gptClasicoBusqueda}
                      onChange={handleGptClasicoBusqueda}
                      style={{ maxWidth: '400px', padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem' }}
                    />
                  </div>
                )}
                {isLoadingMensajes ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Cargando mensajes...</p>
                  </div>
                ) : mensajes.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-content">
                      <i className="fas fa-inbox empty-state-icon"></i>
                      <h3>No hay mensajes</h3>
                      <p>{isGptClasico ? 'Este GPT Clásico aún no tiene mensajes registrados.' : 'Este asistente aún no ha recibido mensajes de estudiantes.'}</p>
                    </div>
                  </div>
                ) : isGptClasico ? (
                  <DataTableWithFilters
                    data={mensajes}
                    columns={columnasGptClasico}
                    searchPlaceholder=""
                    emptyMessage="No se encontraron mensajes"
                    loading={isLoadingMensajes}
                    externalPagination={true}
                    externalTotalItems={paginacionMensajes.total}
                    externalTotalPages={paginacionMensajes.totalPaginas}
                    externalCurrentPage={paginacionMensajes.paginaActual}
                    onPageChange={handlePageChange}
                    defaultItemsPerPage={50}
                  />
                ) : (
                  <DataTableWithFilters
                    data={mensajes}
                    columns={columnasMensajes}
                    searchPlaceholder="Buscar en mensajes..."
                    emptyMessage="No se encontraron mensajes que coincidan con la búsqueda"
                    loading={isLoadingMensajes}
                    externalPagination={true}
                    externalTotalItems={paginacionMensajes.total}
                    externalTotalPages={paginacionMensajes.totalPaginas}
                    externalCurrentPage={paginacionMensajes.paginaActual}
                    onPageChange={handlePageChange}
                    defaultItemsPerPage={50}
                  />
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Modal para ver conversación completa (solo OpenAI) */}
        {!isGptClasico && showConversationModal && selectedMessage && (
          <ConversationModal
            isOpen={showConversationModal}
            onClose={handleCloseConversationModal}
            threadId={selectedMessage.threadId}
            assistantName={selectedAssistant?.nombreAsistente || 'Asistente IA'}
            assistantId={selectedAssistant?.idOpenAIAssistant || 'asst_placeholder'}
            useOpenAI={selectedMessage.conversationSource !== 'db'}
            regionalId={activeRegionalId}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`new-dashboard reports ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {isAdmin ? (
            filteredAdminMenuItems.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${item.id === 'reportes' ? 'active' : ''}`}
                onClick={() => handleNavigate('/admin-dashboard', { state: { tab: item.id } })}
              >
                <i className={item.icon}></i>
                <span>{item.label}</span>
              </button>
            ))
          ) : (
            <>
              <button
                className="nav-item"
                onClick={() => handleNavigate('/dashboard')}
              >
                <i className="fas fa-tachometer-alt"></i>
                <span>Dashboard</span>
              </button>
              <button
                className="nav-item"
                onClick={() => handleNavigate('/my-assistants')}
              >
                <i className="fas fa-list-ul"></i>
                <span>Asistentes OpenAI</span>
              </button>
              {isGptClassicEnabled && (
                <button
                  className="nav-item"
                  onClick={() => handleNavigate('/gpt-assistants')}
                >
                  <i className="fas fa-robot"></i>
                  <span>Asistentes GPT</span>
                </button>
              )}
              <button className="nav-item active">
                <i className="fas fa-chart-bar"></i>
                <span>Reportes</span>
              </button>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{roleLabel}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-chart-bar"></i>
              Reportes
            </h1>
            <p className="page-subtitle">Analizal rendimiento y uso de tus asistentes de IA</p>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar lista"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              Actualizar
            </button>
            
            <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
            
            <div className="user-menu">
              <button 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                    <span className="user-email">{user?.email || 'usuario@utn.edu.ar'}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {/* Table */}
          <div className="content-card">
            <div className="card-header">
              <h2 className="card-title">
                <i className="fas fa-robot"></i>
                Asistentes OpenAI para Reportes
              </h2>
            </div>
            
            <div className="card-content">
              <DataTable
                data={asistentes}
                columns={columns}
                loading={isLoading}
                loadingMessage="Obteniendo asistentes..."
                emptyMessage="No hay asistentes OpenAI disponibles para generar reportes."
                className="reports-table"
              />
            </div>
          </div>

          {isGptClassicEnabled && (
            <div className="content-card" style={{ marginTop: '1.25rem' }}>
              <div className="card-header">
                <h2 className="card-title">
                  <i className="fas fa-robot"></i>
                  Asistentes GPT para Reportes
                </h2>
              </div>

              <div className="card-content">
                <DataTable
                  data={asistentesClasicos}
                  columns={columns}
                  loading={isLoading}
                  loadingMessage="Obteniendo asistentes GPT..."
                  emptyMessage="No hay asistentes GPT disponibles para generar reportes."
                  className="reports-table"
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal para ver conversación completa */}
      {showConversationModal && selectedMessage && (
        <ConversationModal
          isOpen={showConversationModal}
          onClose={handleCloseConversationModal}
          threadId={selectedMessage.threadId}
          assistantName={selectedAssistant?.nombreAsistente || 'Asistente IA'}
          assistantId={selectedAssistant?.idOpenAIAssistant || 'asst_placeholder'}
        />
      )}
    </div>
  );
};

export default Reports;
